import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  InputBase,
  LinearProgress,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useDebounce } from 'use-debounce'
import { T } from '@/helpers/T'
import { useIntlRich } from '@/hooks/useIntlRich'
import { useRoles } from '@/hooks/useRoles'
import { useStateToggle } from '@/hooks/useStateToggle'
import { useSWRAxiosInfinitePagination } from '@/hooks/useSWRAxiosInfinitePagination'
import type { TAssetResourceSimplified } from '@/modules/AssetResource/AssetResource'
import type { IEntitySimplified } from '@/modules/Entity/Entity'
import type { IExpenseSimplified } from '@/modules/Expense/Expense'
import type { TInvoice } from '@/modules/Invoice/Invoice'
import type { IMembershipSimplified } from '@/modules/Membership/Membership'
import type { IPaymentMean } from '@/modules/PaymentMean/PaymentMean'
import type { ISpotlightResponse, ISpotlightSettings } from './Spotlight'
import { getSpotlightResultLink } from './Spotlight.misc'
import { SpotlightResults } from './Spotlight.results'

//	///////////////////////
//	MARK: EXPORTS
const SpotlightInputInternal: React.FC = () => {
  const intl = useIntlRich()
  const navigate = useNavigate()
  const { menuItems } = useRoles()

  // Sources gérées par menu-item ; entity et membership restent sans gate
  // (index ouverts à tout utilisateur connecté côté serveur).
  // role--accounting sert aussi de garde de section au stock (OCC, supply) : on vise la carte des achats.
  const canSearchAccounting = menuItems.has('role--accounting--purchase')
  const canSearchMemberships = true
  const canSearchPaymentMeans = menuItems.has('role--accounting--payment-mean')
  const canSearchAssetResources = menuItems.has('role--human-resources--asset-resource')

  const inputRef = useRef<HTMLElement>(null)
  const inputPlaceholder = useMemo(() => intl.formatMessageTyped({ id: 'lang.search...' }), [intl])

  const [settings, setSettings] = useState<ISpotlightSettings>({
    value: '',
    showExpenses: true,
    showInvoices: true,
    showEntities: true,
    showMemberships: true,
    showPaymentMeans: true,
  })

  const [debouncedSettings] = useDebounce(settings, 300)

  const onChange = useMemo<React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
    () => (e) => {
      const { value } = e.currentTarget

      setSettings((oldSettings) => ({ ...oldSettings, value }))
    },
    [],
  )

  const [modal, showModal, hideModal] = useStateToggle(false)

  const showModalAndFocus = useCallback(() => {
    setSettings((oldSettings) => ({ ...oldSettings, value: '' }))
    showModal()
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [showModal])

  const hideModalAndReset = useCallback(() => {
    setSettings((oldSettings) => ({ ...oldSettings, value: '' }))
    hideModal()
  }, [hideModal])

  useEffect(() => {
    const check = (e: KeyboardEvent) => {
      if (document.activeElement === document.body && e.ctrlKey && e.code === 'Space') {
        showModalAndFocus()
      }
    }

    window.addEventListener('keydown', check)

    return () => window.removeEventListener('keydown', check)
  }, [showModalAndFocus])

  const expensesSwr = useSWRAxiosInfinitePagination<IExpenseSimplified>(10, (index) => {
    if (!canSearchAccounting || debouncedSettings.value.length < 4) {
      return null
    }

    const searchParams = new URLSearchParams()

    searchParams.set('stringFilter', debouncedSettings.value)
    searchParams.set('offset', (index * 10).toString())
    searchParams.set('max', '10')
    searchParams.set('sort', 'id')
    searchParams.set('order', 'desc')

    return `/api/accountMouvementDocument?${searchParams.toString()}`
  })

  const invoicesSwr = useSWRAxiosInfinitePagination<TInvoice>(10, (index) => {
    if (!canSearchAccounting || debouncedSettings.value.length < 4) {
      return null
    }

    const searchParams = new URLSearchParams()

    searchParams.set('stringFilter', debouncedSettings.value)
    searchParams.set('offset', (index * 10).toString())
    searchParams.set('max', '10')
    searchParams.set('sort', 'id')
    searchParams.set('order', 'desc')

    return `/api/invoice?${searchParams.toString()}`
  })

  const entitiesSwr = useSWRAxiosInfinitePagination<IEntitySimplified>(10, (index) => {
    if (debouncedSettings.value.length < 4) {
      return null
    }

    const searchParams = new URLSearchParams()

    searchParams.set('stringFilter', debouncedSettings.value)
    searchParams.set('offset', (index * 10).toString())
    searchParams.set('max', '10')
    searchParams.set('sort', 'id')
    searchParams.set('order', 'desc')

    return `/api/entity?${searchParams.toString()}`
  })

  const membershipsSwr = useSWRAxiosInfinitePagination<IMembershipSimplified>(10, (index) => {
    if (!canSearchMemberships || debouncedSettings.value.length < 4) {
      return null
    }

    const searchParams = new URLSearchParams()

    searchParams.set('stringFilter', debouncedSettings.value)
    searchParams.set('offset', (index * 10).toString())
    searchParams.set('max', '10')
    searchParams.set('sort', 'id')
    searchParams.set('order', 'desc')

    return `/api/membership?${searchParams.toString()}`
  })

  const paymentMeansSwr = useSWRAxiosInfinitePagination<IPaymentMean>(10, (index) => {
    if (!canSearchPaymentMeans || debouncedSettings.value.length < 4) {
      return null
    }

    const searchParams = new URLSearchParams()

    searchParams.set('stringFilter', debouncedSettings.value)
    searchParams.set('offset', (index * 10).toString())
    searchParams.set('max', '10')
    searchParams.set('sort', 'id')
    searchParams.set('order', 'desc')

    return `/api/paymentMean?${searchParams.toString()}`
  })

  const assetResourcesSwr = useSWRAxiosInfinitePagination<TAssetResourceSimplified>(10, (index) => {
    if (!canSearchAssetResources || debouncedSettings.value.length < 4) {
      return null
    }

    const searchParams = new URLSearchParams()

    searchParams.set('stringFilter', debouncedSettings.value)
    searchParams.set('offset', (index * 10).toString())
    searchParams.set('max', '10')
    searchParams.set('sort', 'id')
    searchParams.set('order', 'desc')

    return `/api/assetResource?${searchParams.toString()}`
  })

  const results = useMemo<ISpotlightResponse>(() => {
    const expenses: IExpenseSimplified[] = []
    const invoices: TInvoice[] = []
    const entities: IEntitySimplified[] = []
    const memberships: IMembershipSimplified[] = []
    const paymentMeans: IPaymentMean[] = []
    const assetResources: TAssetResourceSimplified[] = []

    if (expensesSwr.data) {
      for (const data of expensesSwr.data) {
        expenses.push(...data.data.data)
      }
    }

    if (invoicesSwr.data) {
      for (const data of invoicesSwr.data) {
        invoices.push(...data.data.data)
      }
    }

    if (entitiesSwr.data) {
      for (const data of entitiesSwr.data) {
        entities.push(...data.data.data)
      }
    }

    if (membershipsSwr.data) {
      for (const data of membershipsSwr.data) {
        memberships.push(...data.data.data)
      }
    }

    if (paymentMeansSwr.data) {
      for (const data of paymentMeansSwr.data) {
        paymentMeans.push(...data.data.data)
      }
    }
    if (assetResourcesSwr.data) {
      for (const data of assetResourcesSwr.data) {
        assetResources.push(...data.data.data)
      }
    }

    const data = [
      ...entities,
      ...assetResources,
      ...paymentMeans,
      ...memberships,
      ...expenses,
      ...invoices,
    ]

    return {
      entities: {
        data: entities,
        totalCount: entities.length,
      },
      expenses: {
        data: expenses,
        totalCount: expenses.length,
      },
      invoices: {
        data: invoices,
        totalCount: invoices.length,
      },
      memberships: {
        data: memberships,
        totalCount: memberships.length,
      },
      paymentMeans: {
        data: paymentMeans,
        totalCount: paymentMeans.length,
      },
      assetResources: {
        data: assetResources,
        totalCount: assetResources.length,
      },

      data,
      totalCount: data.length,
    }
  }, [
    assetResourcesSwr.data,
    entitiesSwr.data,
    expensesSwr.data,
    invoicesSwr.data,
    membershipsSwr.data,
    paymentMeansSwr.data,
  ])

  const onKeyDown = useCallback<React.KeyboardEventHandler>(
    (e) => {
      if (e.code === 'Enter' && results.totalCount > 0) {
        const link = getSpotlightResultLink(results.data[0])

        if (link) {
          hideModalAndReset()
          navigate(link)
        }
      }
    },
    [hideModalAndReset, navigate, results],
  )

  // Les sources désactivées par menu-item sont neutres (true) dans les agrégats « toutes ».
  const isLoading = useMemo(
    () =>
      (canSearchAssetResources ? assetResourcesSwr.isLoading : true) &&
      entitiesSwr.isLoading &&
      (canSearchAccounting ? expensesSwr.isLoading && invoicesSwr.isLoading : true) &&
      (canSearchMemberships ? membershipsSwr.isLoading : true) &&
      (canSearchPaymentMeans ? paymentMeansSwr.isLoading : true),
    [
      assetResourcesSwr.isLoading,
      entitiesSwr.isLoading,
      expensesSwr.isLoading,
      invoicesSwr.isLoading,
      membershipsSwr.isLoading,
      paymentMeansSwr.isLoading,
      canSearchAccounting,
      canSearchAssetResources,
      canSearchMemberships,
      canSearchPaymentMeans,
    ],
  )

  const isError = useMemo(
    () =>
      (canSearchAssetResources ? !!assetResourcesSwr.error : true) &&
      !!entitiesSwr.error &&
      (canSearchAccounting ? !!expensesSwr.error && !!invoicesSwr.error : true) &&
      (canSearchMemberships ? !!membershipsSwr.error : true) &&
      (canSearchPaymentMeans ? !!paymentMeansSwr.error : true),
    [
      assetResourcesSwr.error,
      entitiesSwr.error,
      expensesSwr.error,
      invoicesSwr.error,
      membershipsSwr.error,
      paymentMeansSwr.error,
      canSearchAccounting,
      canSearchAssetResources,
      canSearchMemberships,
      canSearchPaymentMeans,
    ],
  )

  return (
    <>
      <Button
        variant='outlined'
        color='inherit'
        startIcon={<SearchIcon />}
        onClick={showModalAndFocus}
        sx={(theme) => ({
          ml: 'auto',
          textTransform: 'none',
          backgroundColor: 'rgba(255,255,255,0.15)',
          border: 'none',
          width: 400,
          justifyContent: 'flex-start',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
        })}
      >
        <T id='lang.search...' />
      </Button>
      <Dialog keepMounted open={modal} onClose={hideModalAndReset} maxWidth='md' fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <Box p={2} display='flex' alignItems='center'>
            <SearchIcon color='primary' sx={{ fontSize: 40, mr: 2 }} />
            <InputBase
              inputRef={inputRef}
              value={settings.value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              placeholder={inputPlaceholder}
              sx={{ flex: 1, color: 'primary.main', fontSize: 30, fontWeight: 400 }}
            />
          </Box>
          <Divider />
          <Box p={2}>
            {debouncedSettings.value.length < 4 ? (
              <Alert severity='info'>
                <T id='m.spotlight.min_length' />
              </Alert>
            ) : isLoading ? (
              <LinearProgress />
            ) : isError ? (
              <Alert severity='error'>
                <T id='m.spotlight.error' />
              </Alert>
            ) : (
              <SpotlightResults
                totalCount={results.totalCount}
                results={results.data}
                hideModalAndReset={hideModalAndReset}
              />
            )}
          </Box>
          <Divider />
          <Box p={2} display='flex' justifyContent='space-between' alignItems='center'>
            <Button variant='outlined' color='error' startIcon={<CloseIcon />} onClick={hideModal}>
              <T id='lang.close' />
            </Button>
            <Typography component='p' variant='caption'>
              Courtesy of your beloved IT Department
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const SpotlightInput = React.memo(SpotlightInputInternal) as typeof SpotlightInputInternal
